import { useMemo } from "react";
import { Play, Camera, VideoCamera, CircleNotch, Warning, Sparkle, FileText, Trash, DownloadSimple } from "@phosphor-icons/react";

// Downloads the actual stored (original) file with its real name and type.
export function DownloadLink({ m, className = "" }) {
  return (
    <a href={mediaSrc(m.original_download_url || m.download_url)} download onClick={(e) => e.stopPropagation()} title={`Download ${m.filename}`} aria-label={`Download ${m.filename}`}
      className={`inline-flex items-center justify-center rounded p-1 text-slate-500 hover:text-[#327de3] hover:bg-blue-50 ${className}`} data-testid={`detail-media-download-${m.id}`}><DownloadSimple size={14} weight="bold" /></a>
  );
}

function DelBtn({ m, onDelete }) {
  if (!onDelete) return null;
  return (
    <span role="button" tabIndex={0} title="Delete attachment" aria-label={`Delete ${m.filename}`}
      onClick={(e) => { e.stopPropagation(); onDelete(m); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onDelete(m); } }}
      className="absolute top-1 left-1 z-10 rounded-full bg-black/60 p-1 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 hover:bg-rose-600 transition-[opacity,background-color]"
      data-testid={`detail-media-delete-${m.id}`}><Trash size={13} weight="bold" /></span>
  );
}
import { ExplorerTable } from "./ExplorerTable";
import { mediaSrc, fmtSize, fmtDate, fmtDateTime, CAPTURE_LABELS } from "./serviceApi";

const ATT_SORT = { key: "captured", dir: "asc" };
const REL_SORT = { key: "relevance", dir: "desc" };

export function MediaGallery({ media, onOpen, onDelete }) {
  const photos = media.filter((m) => m.type === "photo");
  const videos = media.filter((m) => m.type === "video");
  const docs = media.filter((m) => m.type === "document");
  return (
    <div className="space-y-4">
      {docs.length > 0 && <Section icon={<FileText size={14} />} title={`Documents (${docs.length})`}>
        <div className="flex flex-wrap gap-2" data-testid="detail-docs">
          {docs.map((m) => <span key={m.id} className="inline-flex items-center gap-1 rounded-md border border-slate-200 pl-2.5 pr-1 py-1 text-xs text-slate-700 hover:border-[#327de3]">
            <a href={mediaSrc(m.original_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5" data-testid={`detail-doc-${m.id}`}><FileText size={14} />{m.filename} <span className="text-slate-400">{fmtSize(m.size)}</span></a>
            <DownloadLink m={m} />
            {onDelete && <button type="button" onClick={() => onDelete(m)} title="Delete attachment" className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`detail-media-delete-${m.id}`}><Trash size={13} /></button>}
          </span>)}
        </div>
      </Section>}
      <Section icon={<Camera size={14} />} title={`Photos (${photos.length})`}>
        {photos.length === 0 ? <Empty text="No photos yet." /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2" data-testid="detail-photos-grid">
            {photos.map((m) => (
              <button key={m.id} type="button" onClick={() => onOpen(m)} className="group relative aspect-[4/3] rounded-md overflow-hidden border border-slate-200 bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#327de3]" data-testid={`detail-photo-${m.id}`}>
                <DelBtn m={m} onDelete={onDelete} />
                <img src={mediaSrc(m.thumb_url || m.display_url)} alt={m.display_name || m.filename} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                {m.stamp_outdated && <span className="absolute top-1 right-1 bg-amber-500 text-white rounded-full p-0.5" title="Stamp outdated"><Warning size={11} weight="bold" /></span>}
              </button>
            ))}
          </div>
        )}
      </Section>
      <Section icon={<VideoCamera size={14} />} title={`Videos (${videos.length})`}>
        {videos.length === 0 ? <Empty text="No videos yet." /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="detail-videos-grid">
            {videos.map((m) => (
              <button key={m.id} type="button" onClick={() => onOpen(m)} aria-label={`Play video ${m.display_name || m.filename}`} className="group relative aspect-video rounded-md overflow-hidden border border-slate-200 bg-slate-900" data-testid={`detail-video-${m.id}`}>
                <DelBtn m={m} onDelete={onDelete} />
                {m.thumb_url && <img src={mediaSrc(m.thumb_url)} alt={m.filename} loading="lazy" className="w-full h-full object-cover opacity-80" />}
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="service-play-btn w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-transform group-hover:scale-110" data-testid={`video-play-icon-${m.id}`}>
                    {m.processing_status === "processing" ? <CircleNotch size={20} color="#000" className="animate-spin" /> : <Play size={22} weight="fill" color="#000" className="ml-0.5" />}
                  </span>
                </span>
                <span className="absolute bottom-0 inset-x-0 px-2 py-1 text-[10px] text-white bg-black/60 truncate text-left">
                  {m.display_name || m.filename}{m.processing_status === "processing" ? " · processing" : m.transcription_status === "done" ? " · transcribed" : ""}
                </span>
              </button>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

export function AttachmentsTable({ media, onOpen }) {
  const columns = useMemo(() => [
    { key: "filename", label: "Name", width: 260, sortValue: (m) => m.display_name || m.filename, render: (m) => <span className="font-medium text-slate-800" title={m.display_name !== m.filename ? `Uploaded as ${m.filename}` : undefined}>{m.display_name || m.filename}</span> },
    { key: "original", label: "Uploaded as", width: 140, sortValue: (m) => m.filename, render: (m) => <span className="text-xs text-slate-500">{m.filename}</span> },
    { key: "type", label: "Type", width: 80, render: (m) => <span className="capitalize">{m.type}</span> },
    { key: "captured", label: "Captured", width: 170, sortValue: (m) => m.capture_at, render: (m) => m.capture_display || fmtDateTime(m.capture_at) },
    { key: "source", label: "Time source", width: 150, sortValue: (m) => m.capture_source, render: (m) => <span className="text-xs text-slate-500">{CAPTURE_LABELS[m.capture_source] || m.capture_source}</span> },
    { key: "uploaded", label: "Uploaded", width: 170, sortValue: (m) => m.uploaded_at, render: (m) => fmtDateTime(m.uploaded_at) },
    { key: "size", label: "Size", width: 90, sortValue: (m) => m.size, render: (m) => fmtSize(m.size) },
    { key: "download", label: "Download", width: 80, sortValue: () => 0, render: (m) => <DownloadLink m={m} /> },
    { key: "ai", label: "AI", width: 70, sortValue: (m) => m.transcription_status || "", render: (m) => (m.transcription_status === "done" ? <Sparkle size={13} weight="fill" className="text-violet-500" /> : "") },
  ], []);
  return (
    <ExplorerTable prefKey="service.detail.attachments" testId="detail-attachments-table" columns={columns} rows={media} onRowClick={onOpen} defaultSort={ATT_SORT} dense emptyText="No attachments." />
  );
}

export function RelatedTable({ related, onOpen }) {
  const columns = useMemo(() => [
    { key: "date", label: "Date", width: 110, sortValue: (r) => r.service_date, render: (r) => fmtDate(r.service_date) },
    { key: "title", label: "Name", width: 200, render: (r) => <span className="font-medium text-slate-800">{r.title}</span> },
    { key: "category_name", label: "Category", width: 110 },
    { key: "unit", label: "Unit", width: 90 },
    { key: "service_person_name", label: "Service Person", width: 140 },
    { key: "rationale", label: "Why related", width: 240, render: (r) => <span className="text-xs text-slate-600" title={r.rationale}>{r.rationale}</span> },
    { key: "relevance", label: "Match", width: 70, render: (r) => <span className="text-[10px] font-bold text-[#327de3]">{r.relevance >= 15 ? "High" : r.relevance >= 5 ? "Good" : "Some"}</span> },
  ], []);
  return (
    <ExplorerTable prefKey="service.detail.related" testId="detail-related-table" columns={columns} rows={related} onRowClick={onOpen} defaultSort={REL_SORT} dense emptyText="No related records at this address." />
  );
}

export function Section({ icon, title, right, children }) {
  return (
    <section>
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-slate-400">{icon}</span>
        <h3 className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{title}</h3>
        <div className="ml-auto">{right}</div>
      </div>
      {children}
    </section>
  );
}

const Empty = ({ text }) => <div className="text-sm text-slate-400 py-2">{text}</div>;
